<template>
  <v-tooltip right :disabled="isMobile() || loading">
    <template v-slot:activator="{ on }">
      <div :style="buttonSize">
        <v-progress-circular
          v-show="loading"
          indeterminate
          class="circleButtonLoader loaderAnimation loaderRotateDash"
          :size="loaderSize"
          :width="6"
          :color="loaderColor"
        />
        <v-container
          fluid
          :class="{ 'cursor-pointer': !disabled, 'circleButtonShadow': !disabled }"
          class="pa-0 circleButton buttonSlotContainer fill-height"
          :style="buttonColorsStyle"
        >
          <v-row
            v-on="on"
            align="center"
            justify="center"
            class="mx-10"
            :class="segmentActive ? 'buttonTopSlot' : 'buttonSlotDefault'"
            @click="$emit('handlePress')"
          >
            <v-col cols="auto">
              <slot name="top"/>
            </v-col>
          </v-row>
          <v-row
            v-on="on"
            align="center"
            justify="center"
            class="mx-1"
            :class="segmentActive ? 'buttonMiddleSlot' : 'buttonSlotDefault'"
            @click="$emit('handlePress')"
          >
            <v-col cols="auto">
              <slot name="middle"/>
            </v-col>
          </v-row>
          <v-tooltip right :disabled="isMobile() || loading">
            <template v-slot:activator="{ on: bottomOn }">
              <v-row
                v-on="segmentActive ? bottomOn : on"
                align="center"
                justify="center"
                class="mx-10 buttonSlotDefault"
                @click="segmentActive
                  ? $emit('handleBottomPress')
                  : $emit('handlePress')"
              >
                <v-col cols="auto">
                  <slot name="bottom"/>
                </v-col>
              </v-row>
            </template>
            <span>{{ bottomTooltip }}</span>
          </v-tooltip>
        </v-container>
      </div>
    </template>
    <span>{{ tooltip }}</span>
  </v-tooltip>
</template>

<script>
import { mapState } from 'vuex';
import constants from '@/constants';
import displayAdaptable from '@/mixins/displayAdaptable';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';

export default {
  name: 'CircularButton',

  mixins: [
    displayAdaptable,
    hasTimePairFeatures,
  ],

  props: {
    colors: Object,
    buttonSize: Object,
    buttonPressed: Boolean,
    tooltip: String,
    bottomTooltip: String,
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(['activeSegmentType']),

    loaderSize() {
      return this.buttonSize.width.replace('px', '');
    },

    loaderColor() {
      if (!this.buttonPressed) return '#8fd56b';

      if (this.activeSegmentType !== constants.TimePairType.SHIFT) return '#ffa051';

      return '#ff7f75';
    },

    buttonColorsStyle() {
      let accessor;
      let secondaryBackground;
      if (this.disabled && !this.buttonPressed && !this.loading) {
        accessor = 'disabled';
      } else if (this.buttonPressed) {
        switch (this.activeSegmentType) {
          case constants.TimePairType.BREAK: {
            if (this.disabled) accessor = 'pressed';
            else accessor = 'onBreak';
            break;
          }
          default: {
            accessor = 'pressed';
          }
        }
        secondaryBackground = (!this.isSegmentTimePairFeatureActive
            || this.activeSegmentType !== constants.TimePairType.SHIFT || this.disabled)
          ? this.getBackgroundColor('pressed')
          : this.getSecondaryBackground('pressed');
      } else {
        accessor = 'unpressed';
      }

      return this.getClockColor(accessor, secondaryBackground);
    },

    segmentActive() {
      return this.isSegmentTimePairFeatureActive && !this.disabled && this.buttonPressed;
    },
  },

  methods: {
    getClockColor(accessor, secondaryBackground) {
      const primaryBackground = this.getBackgroundColor(accessor);
      secondaryBackground = secondaryBackground || this.getBackgroundColor(accessor);
      const primaryBorder = this.colors[accessor].border;
      const secondaryBorder = ((!this.disabled && this.buttonPressed)
        && this.isSegmentTimePairFeatureActive)
        ? primaryBorder
        : this.getBackgroundColor(accessor);

      return {
        background: `linear-gradient(0deg, ${secondaryBackground}, ${secondaryBackground} 33.3%,
        ${secondaryBorder} 1%, ${primaryBackground} 34.3%)`,
        border: `5px solid ${primaryBorder}`,
      };
    },

    getBackgroundColor(accessor) {
      if (this.loading) {
        return this.colors[accessor].loading;
      }
      if (accessor === 'onBreak' && !this.isSegmentTimePairFeatureActive) {
        return this.colors.pressed.background;
      }
      return this.colors[accessor].background;
    },

    getSecondaryBackground(accessor) {
      return this.loading ? this.colors[accessor].secondaryLoading
        : this.colors[accessor].secondaryBackground;
    },
  },
};
</script>

<style scoped>
  .circleButton {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    box-sizing: border-box;
  }

  /*noinspection CssUnusedSymbol*/
  .circleButtonShadow {
    box-shadow: 2px 6px 8px rgba(0, 0, 0, 0.2);
  }

  /*noinspection CssUnusedSymbol*/
  .cursor-pointer, .cursor-pointer >>> span.v-chip {
    cursor: pointer;
  }

  .circleButtonLoader {
    position: absolute;
  }

  .buttonSlotContainer {
    line-height:1em;
    color: white;
  }

  /*Top slot should occupy minority of top 2/3 of full circle when active. Thus allocated 1/3 of top portion.*/
  /*noinspection CssUnusedSymbol*/
  .buttonTopSlot {
    height: 22.2%;
  }

  /*Middle slot should occupy majority of top 2/3 of full circle when active. Thus allocated 2/3 of top portion.*/
  /*noinspection CssUnusedSymbol*/
  .buttonMiddleSlot {
    height: 44.4%;
  }

  /*Button slots should occupy 1/3 of full circle each by default.*/
  .buttonSlotDefault {
    height: 33.3%;
  }

  >>>.v-progress-circular--indeterminate > svg {
    animation: progress-circular-rotate 4s linear infinite;
  }

  /*noinspection CssUnusedSymbol*/
  >>>.v-progress-circular--indeterminate > .v-progress-circular__overlay {
    animation: progress-circular-dash 2s ease-in-out infinite
  }
</style>
